import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomDateTimePicker,
  CustomPdfUploader,
  CustomTwoColInputGroup,
  FixedActionBar,
  CustomMiddleSelect,
  CustomFormNavigationGuard,
  CustomSingleSelect,
  CustomButton,
} from "@ippon/shared-components";
import OtherFeesInput from "@/components/PropertyDetail/shared/OtherFeesInput";
import PersonIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import {
  calculateTotalTsuboUnitPrice,
  formatDateForInput,
  squareMetersToTsubo,
} from "@ippon/shared-utils";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useTsuboLinkedAmounts } from "@/hooks/useTsuboLinkedAmounts";
import { applyApiError } from "@/utils/applyApiError";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { createPropertySection } from "@/store/propertySectionSlice";
import {
  fetchDocumentManagement,
  type DocumentDefinition,
} from "@/store/documentManagementSlice";
import {
  fallbackIndividualDocs,
  fallbackCorporateDocs,
} from "../privateAreaDocsFallback";

export type PrivateAreaFormValues = {
  name: string;
  /** 用途 (residential/shop/office/warehouse/vacant_unit or 自由文字列) */
  section_usage: string;
  /** 収益種別 (rent / ancillary_income / other_income / "" = なし) */
  revenue_type: string;
  /** 階数 (専有部 details) */
  floor?: string;
  /** 間取り */
  floor_plan?: string;
  /** 面積 (㎡) */
  rental_area_m2?: string;
  occupancy_status: "occupied" | "vacant";
  contract_start_date: string;
  contract_end_date: string;
  monthly_rent: string;
  rent_unit_price: string;
  maintenance_fee: string;
  maintenance_fee_unit_price: string;
  security_deposit: string;
  key_money: string;
  contract_remarks: string;
  contractor_type: "corporate" | "individual";

  company_name?: string;
  representative_last_name?: string;
  representative_first_name?: string;
  company_phone_number?: string;
  company_email?: string;
  lease_contract_file?: any[];
  registry_cert_file?: any[];
  seal_cert_file?: any[];
  articles_file?: any[];
  corporate_other_file?: any[];

  last_name?: string;
  first_name?: string;
  phone_number?: string;
  email?: string;
  id_document_file?: any[];
  income_proof_file?: any[];
  guarantor_docs_file?: any[];
  other_file?: any[];
};

export interface PrivateAreaCreateFormProps {
  /** 親物件 ID */
  propertyId: string;
  /** 親物件名（onSuccess で使う、パンくず用） */
  propertyName?: string;
  /** ページモード=page / モーダルモード=modal。デフォルト page */
  mode?: "page" | "modal";
  /** 登録成功時のコールバック。createdId は新規作成された property_section_id */
  onSuccess?: (createdId: string, createdName: string) => void;
  /** 戻る／キャンセル時のコールバック */
  onCancel?: () => void;
  /** modal モードで親に未保存(dirty)状態を伝え、閉じ確認を出させる。 */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * 専有部登録フォーム本体。
 *
 * 専有部内には用途として「貸室・店舗・事務所・倉庫・空区画」が存在し、
 * section_usage プルダウンで選択する。section_usage="residential" のときが
 * いわゆる「貸室」。
 *
 * - mode="page": 既存のフルページ用。FixedActionBar を表示し、`<CustomFormNavigationGuard>` で離脱ガード。
 * - mode="modal": モーダル内表示用。FixedActionBar の代わりにモーダルフッタ（戻る/登録）を表示し、離脱ガードは無効。
 */
const PrivateAreaCreateForm: React.FC<PrivateAreaCreateFormProps> = ({
  propertyId,
  propertyName,
  mode = "page",
  onSuccess,
  onCancel,
  onDirtyChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { record: docMgmtRecord } = useSelector(
    (state: RootState) => state.documentManagement,
  ) as any;
  const { showToast } = useGlobalToast();
  const leftInputContainerWidth = "600px";
  const labelWidth = "120px";

  useEffect(() => {
    if (!docMgmtRecord) {
      dispatch(fetchDocumentManagement() as any);
    }
  }, [dispatch, docMgmtRecord]);

  const individualDocDefs: DocumentDefinition[] = useMemo(() => {
    const list =
      (docMgmtRecord?.documents?.individual as
        | DocumentDefinition[]
        | undefined) || fallbackIndividualDocs;
    return list
      .filter((d) => d.is_enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [docMgmtRecord]);

  const corporateDocDefs: DocumentDefinition[] = useMemo(() => {
    const list =
      (docMgmtRecord?.documents?.corporate as
        | DocumentDefinition[]
        | undefined) || fallbackCorporateDocs;
    return list
      .filter((d) => d.is_enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [docMgmtRecord]);

  const form = useForm<PrivateAreaFormValues>({
    defaultValues: {
      name: "",
      section_usage: "residential",
      revenue_type: "rent",
      floor: "",
      floor_plan: "",
      rental_area_m2: "",
      occupancy_status: "occupied",
      contract_start_date: formatDateForInput(new Date()),
      contract_end_date: (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return formatDateForInput(tomorrow);
      })(),
      monthly_rent: "",
      rent_unit_price: "",
      maintenance_fee: "",
      maintenance_fee_unit_price: "",
      security_deposit: "",
      key_money: "",
      contract_remarks: "",
      contractor_type: "individual",
    },
    mode: "onChange",
  });
  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    setFocus,
    formState: { errors, isSubmitting, dirtyFields },
  } = form;

  const rentalAreaM2 = watch("rental_area_m2");
  const areaTsubo = useMemo(() => {
    const squareMeters = Number(rentalAreaM2);
    return squareMeters > 0 ? squareMetersToTsubo(squareMeters) : undefined;
  }, [rentalAreaM2]);
  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "monthly_rent",
    unitPriceField: "rent_unit_price",
  });
  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "maintenance_fee",
    unitPriceField: "maintenance_fee_unit_price",
  });
  const totalUnitPrice = calculateTotalTsuboUnitPrice(
    Number(watch("rent_unit_price")) || undefined,
    Number(watch("maintenance_fee_unit_price")) || undefined,
  );

  const [otherFees, setOtherFees] = useState<
    Array<{ label: string; amount: string }>
  >([]);

  const ignoredFields = React.useMemo(
    () =>
      new Set(["contractor_type", "contract_start_date", "contract_end_date"]),
    [],
  );
  const hasRealChanges =
    Object.keys(dirtyFields).some(
      (field) => !ignoredFields.has(field as keyof PrivateAreaFormValues),
    ) || otherFees.length > 0;

  React.useEffect(() => {
    onDirtyChange?.(hasRealChanges);
  }, [hasRealChanges, onDirtyChange]);

  const onSubmit = async (data: PrivateAreaFormValues) => {
    try {
      if (!data.name) {
        showToast({
          type: "error",
          message: "必須項目が未入力です（名称）",
        });
        return;
      }

      if (data.contract_start_date && data.contract_end_date) {
        const startDate = new Date(data.contract_start_date);
        const endDate = new Date(data.contract_end_date);
        if (startDate >= endDate) {
          showToast({
            type: "error",
            message: "契約終了日は契約開始日より後の日付を入力してください",
          });
          return;
        }
      }

      const toBase64Items = async (files?: any): Promise<any[]> => {
        const list: File[] = Array.isArray(files) ? files : [];
        const results = await Promise.all(
          list.map(
            (f) =>
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () =>
                  resolve({
                    data: String(reader.result || ""),
                    originalFileName: f.name,
                    mimeType: f.type || "application/pdf",
                  });
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(f);
              }) as Promise<any>,
          ),
        );
        return results.filter(Boolean);
      };

      // 収益種別はユーザーが明示選択 (空文字 = 「なし」)
      const revenueType = data.revenue_type || undefined;
      const isRevenueGenerating = !!revenueType;

      const toNum = (v: unknown): number | undefined => {
        if (v === undefined || v === null || v === "") return undefined;
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : undefined;
      };

      // ── Unit 本体の payload ──
      const payload: any = {
        property_id: propertyId,
        section_category: "private_area",
        section_usage: data.section_usage || "residential",
        is_revenue_generating: isRevenueGenerating,
        revenue_type: revenueType,
        revenue_status: isRevenueGenerating ? "active" : undefined,
        landmark_name: data.name,
        occupancy_status: data.occupancy_status,
        details: {
          ...(data.floor ? { floor: data.floor } : {}),
          ...(data.floor_plan ? { floor_plan: data.floor_plan } : {}),
          ...(data.rental_area_m2
            ? { rental_area_m2: Number(data.rental_area_m2) || undefined }
            : {}),
        },
      };

      // ── 契約情報 (BE が Contracts テーブルに保存。Unit には保存しない) ──
      // 収益あり & 契約者氏名/法人名が入力されていれば BE 側で Contract レコード発番。
      const isCorporate = data.contractor_type === "corporate";
      const hasContractInput = isCorporate
        ? !!data.company_name
        : !!(data.last_name || data.first_name);
      if (isRevenueGenerating && hasContractInput && data.contract_start_date) {
        const contractorName = isCorporate
          ? data.company_name || ""
          : [data.last_name, data.first_name].filter(Boolean).join(" ");
        const contactPerson = isCorporate
          ? [data.representative_last_name, data.representative_first_name]
              .filter(Boolean)
              .join(" ")
          : undefined;

        // 書類 (Contract.documents)
        const documentsForm: Record<string, any> =
          (getValues as any)("documents") || {};
        const contractDocuments: any[] = [];
        let docCounter = 0;
        const nowIso = new Date().toISOString();
        for (const [docType, files] of Object.entries(documentsForm)) {
          const encoded = await toBase64Items(files as any);
          for (const f of encoded) {
            contractDocuments.push({
              document_id: `${Date.now()}-${docCounter++}`,
              category: docType,
              file_name: f?.originalFileName || `document-${docCounter}`,
              s3_key: f?.data || "",
              uploaded_at: nowIso,
            });
          }
        }

        const validFees = otherFees.filter((f) => f.label.trim());

        payload.contract = {
          contractor_type: data.contractor_type,
          contractor_name: contractorName,
          ...(isCorporate && { company_name: data.company_name || "" }),
          ...(isCorporate &&
            contactPerson && { contact_person: contactPerson }),
          phone: isCorporate ? data.company_phone_number : data.phone_number,
          email: isCorporate ? data.company_email : data.email,
          start_date: data.contract_start_date,
          end_date: data.contract_end_date || undefined,
          monthly_rent: toNum(data.monthly_rent),
          rent_unit_price: toNum(data.rent_unit_price),
          maintenance_fee: toNum(data.maintenance_fee),
          maintenance_fee_unit_price: toNum(data.maintenance_fee_unit_price),
          security_deposit: toNum(data.security_deposit),
          key_money: toNum(data.key_money),
          contract_remarks: data.contract_remarks || "",
          ...(validFees.length > 0 && {
            other_fees: validFees.map((f) => ({
              label: f.label,
              amount: f.amount ? Number(f.amount) : 0,
            })),
          }),
          ...(contractDocuments.length > 0 && {
            documents: contractDocuments,
          }),
        };
      }

      const result = await dispatch(createPropertySection(payload));
      if ((createPropertySection as any).rejected.match(result)) {
        // API のフィールド単位バリデーション (例: 名称重複) は該当 input 下に
        // インライン表示する。それ以外 (500/通信失敗等) はトースト。
        applyApiError(result.payload, {
          setError,
          setFocus,
          fieldMap: { landmark_name: "name" },
          onFallback: (p) =>
            showToast({
              type: "error",
              message: p.message || "登録に失敗しました",
            }),
        });
        return;
      }

      showToast({
        type: "success",
        message: "専有部の登録に成功しました",
      });

      const propertySectionId = (result.payload as any)?.property_section_id;
      if (propertySectionId) {
        onSuccess?.(propertySectionId, data.name);
      } else {
        onSuccess?.("", data.name);
      }
      // propertyName を直接は使わないが、呼び出し側コールバックで参照する設計
      void propertyName;
    } catch (error) {
      console.error("専有部登録エラー:", error);
      const apiMessage =
        error instanceof Error && error.message
          ? error.message
          : "専有部の登録に失敗しました";
      showToast({
        type: "error",
        message: apiMessage,
      });
    }
  };

  const formBody = (
    <>
      {/* 基本情報 */}
      <Box mb={3}>
        <CustomSectionTitle title="基本情報" size="small" />
        <Box sx={{ mt: 2, maxWidth: "100%" }}>
          <CustomTextInput
            label="名称"
            name="name"
            register={register}
            errors={errors}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp={"100%"}
            isRequired
          />
          <CustomSingleSelect
            name="section_usage"
            control={control}
            label="用途"
            options={[
              { value: "residential", label: "貸室" },
              { value: "shop", label: "店舗" },
              { value: "office", label: "事務所" },
              { value: "warehouse", label: "倉庫" },
              { value: "vacant_unit", label: "空区画" },
            ]}
            isRequired
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp="100%"
          />
          <CustomSingleSelect
            name="revenue_type"
            control={control}
            label="収益種別"
            options={[
              { value: "", label: "なし" },
              { value: "rent", label: "賃料" },
              { value: "ancillary_income", label: "付帯収益" },
              { value: "other_income", label: "その他収入" },
            ]}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp="100%"
          />
          <CustomTextInput
            label="階数"
            name="floor"
            register={register}
            errors={errors}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp={"100%"}
          />
          <CustomTextInput
            label="間取り"
            name="floor_plan"
            register={register}
            errors={errors}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp={"100%"}
          />
          <CustomTextInput
            label="面積(㎡)"
            name="rental_area_m2"
            register={register}
            errors={errors}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp={"100%"}
          />
          <CustomSingleSelect
            name="occupancy_status"
            control={control}
            label="ステータス"
            options={[
              { value: "occupied", label: "入居中" },
              { value: "vacant", label: "空室" },
            ]}
            isRequired
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp="100%"
          />
        </Box>
      </Box>

      {/* 契約情報（入居中の場合のみ表示） */}
      {watch("occupancy_status") === "occupied" && (
        <Box mb={3}>
          <CustomSectionTitle title="契約情報" size="small" />
          <Box sx={{ mt: 2, maxWidth: "100%" }}>
            <Box sx={{ pl: { lg: 0, xs: 0 }, mb: 2 }}>
              <CustomMiddleSelect
                label="契約者種別"
                options={[
                  {
                    value: "individual",
                    label: "個人",
                    icon: <PersonIcon sx={{ fontSize: 18 }} />,
                  },
                  {
                    value: "corporate",
                    label: "法人",
                    icon: <BusinessIcon sx={{ fontSize: 18 }} />,
                  },
                ]}
                value={watch("contractor_type") as any}
                onChange={(val) => {
                  setValue("contractor_type", val as any, {
                    shouldDirty: true,
                    shouldValidate: false,
                    shouldTouch: false,
                  });
                }}
                width={leftInputContainerWidth}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="月額賃料（税別）"
                name="monthly_rent"
                register={register}
                errors={errors}
                inputWidth="100%"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
              />
              <CustomTextInput
                label="賃料坪単価"
                name="rent_unit_price"
                register={register}
                errors={errors}
                inputWidth="100%"
                type="number"
                showYen
                endAdornmentText="円/坪"
                watch={watch}
                setValue={setValue}
                disabled={!areaTsubo}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="共益費"
                name="maintenance_fee"
                register={register}
                errors={errors}
                inputWidth="100%"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
              />
              <CustomTextInput
                label="共益費坪単価"
                name="maintenance_fee_unit_price"
                register={register}
                errors={errors}
                inputWidth="100%"
                type="number"
                showYen
                endAdornmentText="円/坪"
                watch={watch}
                setValue={setValue}
                disabled={!areaTsubo}
              />
            </Box>
            <Typography fontSize={12} sx={{ mb: 2 }}>
              賃料合計坪単価:{" "}
              {totalUnitPrice === undefined
                ? "-"
                : `${totalUnitPrice.toLocaleString("ja-JP")}円/坪`}
            </Typography>
            <CustomTextInput
              label="敷金"
              name="security_deposit"
              register={register}
              errors={errors}
              labelWidth={labelWidth}
              labelWidthSp="47%"
              inputWidth={leftInputContainerWidth}
              inputWidthSp={"100%"}
              type="number"
              showYen
              watch={watch}
              setValue={setValue}
            />
            <CustomTextInput
              label="礼金"
              name="key_money"
              register={register}
              errors={errors}
              labelWidth={labelWidth}
              labelWidthSp="47%"
              inputWidth={leftInputContainerWidth}
              inputWidthSp={"100%"}
              type="number"
              showYen
              watch={watch}
              setValue={setValue}
            />
            <OtherFeesInput
              fees={otherFees}
              onChange={setOtherFees}
              labelWidth={labelWidth}
              inputWidth={leftInputContainerWidth}
            />
          </Box>
        </Box>
      )}

      {/* 個人情報セクション */}
      {watch("occupancy_status") === "occupied" &&
        watch("contractor_type") === "individual" && (
          <Box mb={3}>
            <CustomSectionTitle title="個人情報" size="small" />
            <Box sx={{ mt: 2, maxWidth: "100%" }}>
              <CustomTwoColInputGroup
                label="氏名"
                firstName="last_name"
                lastName="first_name"
                placeholderOne="氏"
                placeholderTwo="名"
                register={register}
                errors={errors}
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
              />
              <CustomTextInput
                label="電話番号"
                name="phone_number"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                type="phoneNumber"
              />
              <CustomTextInput
                label="メールアドレス"
                name="email"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                type="email"
              />
              {(individualDocDefs || []).map((def) => (
                <Box sx={{ mt: 2 }} key={def.document_type}>
                  <CustomPdfUploader
                    name={`documents.${def.document_type}`}
                    label={def.display_name}
                    register={register as any}
                    setValue={setValue as any}
                    errors={errors}
                    width={leftInputContainerWidth}
                    type="multiple"
                    accept={".pdf"}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

      {/* 法人情報セクション */}
      {watch("occupancy_status") === "occupied" &&
        watch("contractor_type") === "corporate" && (
          <Box mb={3}>
            <CustomSectionTitle title="法人情報" size="small" />
            <Box sx={{ mt: 2, maxWidth: "100%" }}>
              <CustomTextInput
                label="法人名"
                name="company_name"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
              />
              <CustomTwoColInputGroup
                label="代表者氏名"
                firstName="representative_last_name"
                lastName="representative_first_name"
                placeholderOne="氏"
                placeholderTwo="名"
                register={register}
                errors={errors}
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
              />
              <CustomTextInput
                label="電話番号"
                name="company_phone_number"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                type="phoneNumber"
              />
              <CustomTextInput
                label="メールアドレス"
                name="company_email"
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                type="email"
              />
              {(corporateDocDefs || []).map((def) => (
                <Box sx={{ mt: 2 }} key={def.document_type}>
                  <CustomPdfUploader
                    name={`documents.${def.document_type}`}
                    label={def.display_name}
                    register={register as any}
                    setValue={setValue as any}
                    errors={errors}
                    width={leftInputContainerWidth}
                    type="multiple"
                    accept={".pdf"}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

      {/* 契約条件セクション (occupied + 収益あり時のみ表示) */}
      {watch("occupancy_status") === "occupied" && !!watch("revenue_type") && (
        <Box mb={3}>
          <CustomSectionTitle title="契約条件" size="small" />
          <Box sx={{ mt: 2, maxWidth: "100%" }}>
            <Box sx={{ display: "block", width: "100%", marginBottom: 2 }}>
              <Typography
                color="#3E3E3E"
                fontWeight={700}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  fontSize: { xs: "10px", sm: "12px" },
                  width: { xs: "40%", sm: "100px" },
                }}
              >
                契約開始日
              </Typography>
              <Controller
                name="contract_start_date"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomDateTimePicker
                    onChange={onChange}
                    width={leftInputContainerWidth}
                    value={value}
                    showTime={false}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "block", width: "100%", marginBottom: 2 }}>
              <Typography
                color="#3E3E3E"
                fontWeight={700}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  fontSize: { xs: "10px", sm: "12px" },
                  width: { xs: "40%", sm: "100px" },
                }}
              >
                契約終了日
              </Typography>
              <Controller
                name="contract_end_date"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomDateTimePicker
                    onChange={onChange}
                    width={leftInputContainerWidth}
                    value={value}
                    showTime={false}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="月額賃料(税別)"
                name="monthly_rent"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
                inputWidth="100%"
              />
              <CustomTextInput
                label="賃料坪単価"
                name="rent_unit_price"
                type="number"
                showYen
                endAdornmentText="円/坪"
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
                inputWidth="100%"
                disabled={!areaTsubo}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="共益費"
                name="maintenance_fee"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
                inputWidth="100%"
              />
              <CustomTextInput
                label="共益費坪単価"
                name="maintenance_fee_unit_price"
                type="number"
                showYen
                endAdornmentText="円/坪"
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
                inputWidth="100%"
                disabled={!areaTsubo}
              />
            </Box>
            <Typography fontSize={12} sx={{ mb: 2 }}>
              賃料合計坪単価:{" "}
              {totalUnitPrice === undefined
                ? "-"
                : `${totalUnitPrice.toLocaleString("ja-JP")}円/坪`}
            </Typography>
            <CustomTextInput
              label="敷金"
              name="security_deposit"
              type="number"
              showYen
              watch={watch}
              setValue={setValue}
              register={register}
              errors={errors}
              labelWidth={labelWidth}
              labelWidthSp="47%"
              inputWidth={leftInputContainerWidth}
              inputWidthSp={"100%"}
            />
            <CustomTextInput
              label="礼金"
              name="key_money"
              type="number"
              showYen
              watch={watch}
              setValue={setValue}
              register={register}
              errors={errors}
              labelWidth={labelWidth}
              labelWidthSp="47%"
              inputWidth={leftInputContainerWidth}
              inputWidthSp={"100%"}
            />
            <OtherFeesInput
              fees={otherFees}
              onChange={setOtherFees}
              inputWidth={leftInputContainerWidth}
            />
          </Box>
        </Box>
      )}

      {/* その他セクション */}
      <Box mb={3}>
        <CustomSectionTitle title="その他" size="small" />
        <Box sx={{ mt: 2, maxWidth: "100%" }}>
          <CustomTextInput
            label="備考"
            name="contract_remarks"
            register={register}
            errors={errors}
            labelWidth={labelWidth}
            labelWidthSp="47%"
            inputWidth={leftInputContainerWidth}
            inputWidthSp={"100%"}
            multiline
            autoResize
            minRows={3}
            maxRows={20}
          />
        </Box>
      </Box>
    </>
  );

  if (mode === "modal") {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box className="registerModalBody">{formBody}</Box>
        <Box className="registerModalFooter">
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => onCancel?.()}
            disabled={isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
          <CustomButton
            label="新規登録"
            type="submit"
            buttonCategory="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <CustomFormNavigationGuard
      hasFormChanged={hasRealChanges}
      isSubmitting={isSubmitting}
    >
      {() => (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {formBody}
          <FixedActionBar
            submitLabel="登録"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            showCancel
            cancelLabel="戻る"
            onCancel={() => onCancel?.()}
          />
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default PrivateAreaCreateForm;
